import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  Connection,
  ConnectionsResponse,
  CreateOrganizationPayload,
  CreateOrganizationResponse,
  GenerateAccessTicketPayload,
  GetOrganizationResponse,
  Organization,
  SSOAccessTicket,
  SSOAccessTicketResponse,
  SSOConnection,
  SSOConnectionsResponse,
  UpdateOrganizationPayload,
  UpdateOrganizationResponse,
} from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  CREATE: "/api/v2/user/:user_id/organizations",
  GET: "/api/v2/org/:organization_id/organization",
  UPDATE: "/api/v2/org/:organization_id/organization",
  DELETE: "/api/v2/org/:organization_id/organization",
  SSO: {
    LIST: "/api/v2/org/:organization_id/sso/connections",
    ACCESS_TICKET: "/api/v2/org/:organization_id/sso/access-ticket",
  },
  CONNECTIONS: {
    LIST: "/api/v2/org/:organization_id/connections",
  },
};

export class OrganizationAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  /**
   * Create organization for a specific user (admin function)
   */
  async createForUser(
    userId: string,
    payload: CreateOrganizationPayload,
    config?: AxiosRequestConfig,
  ): Promise<Organization> {
    const response = await this.client.axios.post<CreateOrganizationResponse>(
      route(ENDPOINTS.CREATE, { user_id: userId }),
      payload,
      config,
    );

    return response.data.data.organization;
  }

  /**
   * Get organization details for a specific organization (admin function)
   */
  async getById(
    orgId: string,
    config?: AxiosRequestConfig,
  ): Promise<Organization> {
    const response = await this.client.axios.get<GetOrganizationResponse>(
      route(ENDPOINTS.GET, { organization_id: orgId }),
      config,
    );

    return response.data.data.organization;
  }

  /**
   * Update organization for a specific organization (admin function)
   */
  async updateById(
    orgId: string,
    payload: UpdateOrganizationPayload,
    config?: AxiosRequestConfig,
  ): Promise<Organization> {
    const response = await this.client.axios.put<UpdateOrganizationResponse>(
      route(ENDPOINTS.UPDATE, { organization_id: orgId }),
      payload,
      config,
    );

    return response.data.data.organization;
  }

  /**
   * Delete organization for a specific organization (admin function)
   */
  async deleteById(orgId: string, config?: AxiosRequestConfig): Promise<void> {
    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, { organization_id: orgId }),
      config,
    );
  }

  /**
   * Create a new organization for the current user
   */
  async create(
    payload: CreateOrganizationPayload,
    config?: AxiosRequestConfig,
  ): Promise<Organization> {
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<CreateOrganizationResponse>(
      route(ENDPOINTS.CREATE, { user_id: userId || "" }),
      payload,
      config,
    );

    return response.data.data.organization;
  }

  /**
   * Get all SSO connections for the organization
   */
  async listSSOConnections(
    config?: AxiosRequestConfig,
  ): Promise<SSOConnection[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<SSOConnectionsResponse>(
      route(ENDPOINTS.SSO.LIST, { organization_id: orgId || "" }),
      config,
    );

    return response.data.data.connections;
  }

  /**
   * Generate an access ticket for SSO configuration
   */
  async generateAccessTicket(
    payload?: GenerateAccessTicketPayload,
    config?: AxiosRequestConfig,
  ): Promise<SSOAccessTicket> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.post<SSOAccessTicketResponse>(
      route(ENDPOINTS.SSO.ACCESS_TICKET, {
        organization_id: orgId || "",
      }),
      payload,
      config,
    );

    return response.data.data.ticket;
  }

  /**
   * Get all auth connections
   */
  async listConnections(config?: AxiosRequestConfig): Promise<Connection[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<ConnectionsResponse>(
      route(ENDPOINTS.CONNECTIONS.LIST, {
        organization_id: orgId || "",
      }),
      config,
    );
    return response.data.data.connections;
  }

  /**
   * Get organization details
   */
  async get(config?: AxiosRequestConfig): Promise<Organization> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<GetOrganizationResponse>(
      route(ENDPOINTS.GET, { organization_id: orgId || "" }),
      config,
    );

    return response.data.data.organization;
  }

  /**
   * Update organization details
   */
  async update(
    payload: UpdateOrganizationPayload,
    config?: AxiosRequestConfig,
  ): Promise<Organization> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.put<UpdateOrganizationResponse>(
      route(ENDPOINTS.UPDATE, { organization_id: orgId || "" }),
      payload,
      config,
    );

    return response.data.data.organization;
  }

  /**
   * Delete organization
   */
  async delete(config?: AxiosRequestConfig): Promise<void> {
    const orgId = await this.client.getOrgID();

    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, { organization_id: orgId || "" }),
      config,
    );
  }
}
